/**
 * HR-Bereich — typisierte API-Wrapper (Phase 8).
 * Endpunkte: docs/hr/FRONTEND_API_CONTRACT.md. Nutzt callBackend (Envelope) bzw.
 * direkten fetch für den binären AU-Abruf.
 */

import { callBackend, WORKER_URL, ApiError } from "./client";
import { getToken } from "../auth/token";

// ---------- Stammdaten ----------
export type HrStammObj = {
  person_id: string; vertragsart: string; wochenstunden: string;
  urlaubseinheit: "tage" | "stunden" | string; vollzeit_arbeitstage_woche: string;
  oe_arbeitstage_woche: string; urlaub_mindest_tage: string; urlaub_zusatz_tage: string;
  geburtsdatum: string; telefon: string; adresse: string; plz_ort: string;
  notfallkontakt_name: string; notfallkontakt_telefon: string; asana_gid: string;
  aktualisiert_am: string; aktualisiert_von: string; urlaub_tagessatz_modus: "default" | "real" | string;
  kostenstellen_bereich: string;
};

export type HrPensum = {
  modus: "default" | "real";
  geplant_arbeitstage: number; geplant_std_pro_tag: number;
  real_arbeitstage: number; real_std_pro_tag: number;
  genug_historie: boolean; erfasste_tage: number;
};
export const hrStammdatenGet = (p: { person_id?: string; with_pensum?: boolean } = {}) =>
  callBackend<{ person_id: string; stammdaten: HrStammObj | null; pensum?: HrPensum }>("/api/v1/hr/stammdaten/get", p);

export const hrStammdatenSet = (p: { person_id: string } & Partial<Record<keyof HrStammObj, string | number>>) =>
  callBackend<{ person_id: string }>("/api/v1/hr/stammdaten/set", p);

// ---------- Vertragshistorie (#3) ----------
export type HrEndeGrund = "" | "wechsel" | "befristung_ende" | "austritt";
export type HrVertragsPeriode = {
  historie_id: string; person_id: string; gueltig_ab: string; gueltig_bis: string;
  vertragsart: string; wochenstunden: string; urlaubseinheit: string;
  vollzeit_arbeitstage_woche: string; oe_arbeitstage_woche: string; urlaub_mindest_tage: string; bruttogehalt: string; kostenstelle: string; stellenbezeichnung: string; notiz: string; erfasst_von: string; erfasst_am: string;
  /** E10/AUS-A: "" | wechsel | befristung_ende | austritt. `austritt` setzt `vertragsende` in CT_Mitarbeiter. */
  ende_grund: HrEndeGrund;
};
export const hrVertragshistorieList = (p: { person_id?: string } = {}) =>
  callBackend<{ person_id: string; perioden: HrVertragsPeriode[] }>("/api/v1/hr/vertragshistorie/list", p);
export const hrVertragshistorieSet = (p: {
  person_id: string; gueltig_ab: string; gueltig_bis?: string; vertragsart: string;
  wochenstunden: string | number; urlaubseinheit: "tage" | "stunden"; vollzeit_arbeitstage_woche?: string | number; oe_arbeitstage_woche?: string | number; urlaub_mindest_tage?: string | number; bruttogehalt?: string | number; kostenstelle?: string; stellenbezeichnung?: string; notiz?: string; ende_grund?: HrEndeGrund;
}) => callBackend<{ person_id: string; historie_id: string }>("/api/v1/hr/vertragshistorie/set", p);
export const hrVertragshistorieEdit = (p: {
  person_id: string; original_historie_id: string; gueltig_ab: string; gueltig_bis?: string; vertragsart: string;
  wochenstunden: string | number; urlaubseinheit: "tage" | "stunden"; vollzeit_arbeitstage_woche?: string | number; oe_arbeitstage_woche?: string | number; urlaub_mindest_tage?: string | number; bruttogehalt?: string | number; kostenstelle?: string; stellenbezeichnung?: string; notiz?: string; ende_grund?: HrEndeGrund;
}) => callBackend<{ person_id: string; historie_id: string }>("/api/v1/hr/vertragshistorie/edit", p);
export const hrVertragshistorieDelete = (p: { person_id: string; historie_id: string }) =>
  callBackend<{ person_id: string; historie_id: string; removed: boolean }>("/api/v1/hr/vertragshistorie/delete", p);

// ---------- Urlaub ----------
export type HrUrlaubKonto = {
  person_id: string; jahr: number; einheit: "tage" | "stunden";
  anspruch_mindest: number; anspruch_zusatz: number; uebertrag_mindest: number; uebertrag_zusatz: number;
  verbraucht_mindest: number; verbraucht_zusatz: number; rest_mindest: number; rest_zusatz: number; rest_gesamt: number;
  /** Std pro Urlaubstag (nur Stunden-MA, sonst 0) — für Tage-Äquivalent-Anzeige. */
  tagessatz_stunden?: number;
  /** Wochenstunden des MA (nur Stunden-MA relevant) — Cap/Default fürs pro-Woche-Formular. */
  wochenstunden?: number;
  /** Ungedeckter Verbrauch (nur aus HR-Nachträgen, sonst 0) — `rest_*` ist bei 0 gekappt. */
  ueberzug?: number;
};
export type HrAntrag = {
  antrag_id: string; person_id: string; von: string; bis: string; halbe_tage: string; kommentar_ma: string;
  status: string; eingereicht_am: string; geprueft_von: string; geprueft_am: string;
  kommentar_leitung: string; verbrauch_detail: string; abwesenheit_synced: string;
  /** Stunden-MA: pro-Woche-Stunden (JSON-String) {wochen:[{ab,stunden,voll}],summe}. Leer bei Tage-MA. */
  stunden_detail?: string;
};
/** Eine Urlaubswoche eines Stunden-MA. */
export type HrStundenWoche = { ab: string; stunden: number; voll: boolean };

export const hrUrlaubKonto = (p: { person_id?: string; jahr?: number } = {}) =>
  callBackend<HrUrlaubKonto>("/api/v1/hr/urlaub/konto", p);
export const hrUrlaubAntrag = (p: { person_id?: string; von: string; bis: string; halbe_tage?: string; kommentar_ma?: string; stunden_pro_woche?: HrStundenWoche[] | Array<{ ab: string; stunden: number }> }) =>
  callBackend<{ antrag_id: string; status: string }>("/api/v1/hr/urlaub/antrag", p);
/**
 * HR-Nachtrag: Urlaub FÜR einen Mitarbeiter eintragen (Admin/HR-Leiter). Ein
 * Vorgang — wird sofort als „Genehmigt" gebucht. `ueberzug > 0` heißt: der
 * Anspruch deckte den Zeitraum nicht, gebucht wurde trotzdem (bewusst, s.
 * Handler `hr.urlaub.eintragen`).
 */
export const hrUrlaubEintragen = (p: { person_id: string; von: string; bis: string; halbe_tage?: string; kommentar?: string; stunden_pro_woche?: Array<{ ab: string; stunden: number }> }) =>
  callBackend<{ antrag_id: string; person_id: string; status: string; betrag: number; werktage: number; einheit: string; rest_gesamt: number; ueberzug: number }>("/api/v1/hr/urlaub/eintragen", p);
/** Marker am Anfang von `kommentar_leitung` bei HR-Nachträgen (s. Backend `HR_EINTRAG_MARKER`). */
export const HR_EINTRAG_MARKER = "Von HR eingetragen";
/** Ist dieser Antrag ein HR-Nachtrag (statt eines eigenen Antrags des MA)? */
export const istHrEintrag = (a: { kommentar_leitung?: string }): boolean =>
  (a.kommentar_leitung ?? "").startsWith(HR_EINTRAG_MARKER);
export const hrUrlaubAntraege = (p: { person_id?: string; alle?: boolean } = {}) =>
  callBackend<{ antraege: HrAntrag[]; scope: string }>("/api/v1/hr/urlaub/antraege", p);
export const hrUrlaubEntscheiden = (p: { antrag_id: string; entscheidung: "genehmigt" | "abgelehnt"; kommentar_leitung?: string }) =>
  callBackend<{ antrag_id: string; status: string; betrag?: number; werktage?: number; rest_gesamt?: number; einheit?: string }>("/api/v1/hr/urlaub/entscheiden", p);
export const hrUrlaubStorno = (p: { antrag_id: string; kommentar?: string }) =>
  callBackend<{ antrag_id: string; status: string }>("/api/v1/hr/urlaub/storno", p);
export const hrGratulation = (p: { name: string; tage?: number; datum?: string; anlass?: "geburtstag" | "jubilaeum"; jahre?: number }) =>
  callBackend<{ sent: boolean; channel: string }>("/api/v1/hr/gratulation", p);
export const hrUrlaubKontoAnpassen = (p: { person_id: string; jahr?: number; anspruch_mindest?: number; anspruch_zusatz?: number; uebertrag_mindest?: number; uebertrag_zusatz?: number; uebertrag_kappung?: number }) =>
  callBackend<{ person_id: string; jahr: number; rest_gesamt: number }>("/api/v1/hr/urlaub/konto/anpassen", p);

// ---------- Zeiterfassung ----------
export type HrZeitEintrag = {
  /** Surrogat-PK der Zeile (E3/K1a) — eindeutige Adressierung, auch bei
   *  Stempel-Duplikaten mit identischer eintrag_id. */
  id: number;
  eintrag_id: string; person_id: string; datum: string; start: string; ende: string; pause_min: string;
  dauer_netto: string; ort: string; taetigkeit: string; quelle: "stempel" | "manuell" | string;
  status: "gebucht" | "korrektur_offen" | string; erfasst_am: string; korrigiert_von: string; korrigiert_am: string;
};
export type HrStempelStatus =
  | { running: false }
  | { running: true; status: "aktiv" | "pause"; start_ts: number; pause_summe_sek: number; netto_hm: string; ort: string; notiz: string };

export const hrStempelStart = (p: { ort?: string; notiz?: string } = {}) =>
  callBackend<{ person_id: string; status: string; start_ts: number }>("/api/v1/hr/stempel/start", p);
export const hrStempelPause = () => callBackend<{ person_id: string; status: string }>("/api/v1/hr/stempel/pause", {});
export const hrStempelResume = () => callBackend<{ person_id: string; status: string; pause_summe_sek: number }>("/api/v1/hr/stempel/resume", {});
export const hrStempelStatus = () => callBackend<HrStempelStatus>("/api/v1/hr/stempel/status", {});
export const hrStempelStop = (p: { taetigkeit?: string } = {}) =>
  callBackend<{ eintrag_id: string; datum: string; start: string; ende: string; pause_min: number; dauer_netto: string; ueber10h: boolean; pauseDefizitMin: number; gesetzlMindestpauseMin: number; ruhezeitVerletzt?: boolean; ruhezeitStd?: string }>("/api/v1/hr/stempel/stop", p);
export const hrZeitRead = (p: { person_id?: string; monat?: string } = {}) =>
  callBackend<{ person_id: string; monat: string; eintraege: HrZeitEintrag[]; summe_hm: string; summe_min: number; soll_min?: number; soll_hm?: string; saldo_min?: number; saldo_hm?: string }>("/api/v1/hr/zeit/read", p);
export const hrZeitKorrekturAntrag = (p: { person_id?: string; datum: string; art: "aenderung" | "neu"; feld?: string; neu_wert?: string; start?: string; ende?: string; pause_min?: string | number; taetigkeit?: string; ort?: string; begruendung: string }) =>
  callBackend<{ korrektur_id: string; status: string }>("/api/v1/hr/zeit/korrektur-antrag", p);
export const hrZeitKorrekturEntscheiden = (p: { korrektur_id: string; entscheidung: "genehmigt" | "abgelehnt" }) =>
  callBackend<{ korrektur_id: string; status: string }>("/api/v1/hr/zeit/korrektur-entscheiden", p);
export type HrZeitKorrektur = { korrektur_id: string; person_id: string; datum: string; feld: string; alt_wert: string; neu_wert: string; begruendung: string; status: string; entschieden_von: string; entschieden_am: string; erstellt_am: string };
export const hrZeitKorrekturList = () => callBackend<{ korrekturen: HrZeitKorrektur[] }>("/api/v1/hr/zeit/korrektur-list", {});
export type HrZeitTeam = {
  monat: string;
  saldi: Array<{ person_id: string; ist: string; soll: string; saldo: string; saldoKind: string; flags: number }>;
  compliance: Array<{ person_id: string; datum: string; art: string; detail: string; schwere: string; quittiert: boolean }>;
  kpis: { teamSaldo: string; teamSaldoKind: string; flags: number; ueber10h: number; ruhezeit?: number; saldoWarnCount: number };
  saldoWarn: Array<{ person_id: string; saldo: string; saldoMin: number; kind: "ueber" | "minus"; abMonat: string }>;
  saldoTracking: boolean;
  trackingStart: string;
};
export const hrZeitTeamUebersicht = (p: { monat?: string } = {}) =>
  callBackend<HrZeitTeam>("/api/v1/hr/zeit/team-uebersicht", p);
// #5 Dienstjahre/Jubiläen + Werdegang — abgeleitetes Eintrittsdatum + erste/aktuelle Vertragsart je MA.
export type HrDienstjahrEntry = { person_id: string; eintritt: string; erste_vertragsart: string; aktuelle_vertragsart: string };
export const hrDienstjahre = () => callBackend<{ eintraege: HrDienstjahrEntry[]; meilensteine: number[] }>("/api/v1/hr/dienstjahre", {});
// #1: Direkte Korrektur (ohne Antrag) + ArbZG-Warnung quittieren (HR/Admin).
export const hrZeitKorrekturDirekt = (p: { person_id: string; datum: string; start?: string; ende?: string; pause_min?: string | number; ort?: string; taetigkeit?: string }) =>
  callBackend<{ person_id: string; datum: string }>("/api/v1/hr/zeit/korrektur-direkt", p);
// Selbst-Eintragung: MA bucht eigene Zeit direkt (Nachtrag/Korrektur, ohne HR-Freigabe).
/** Ein Zeitblock der Mehrfach-Erfassung (B10a). */
export type HrZeitBlock = { start: string; ende: string; pause_min?: string | number; ort?: string; taetigkeit?: string };
/**
 * Zeit selbst buchen. Drei Modi, die sich ausschließen:
 *  • `id` (bevorzugt) bzw. `eintrag_id` (Legacy) → genau diesen Block bearbeiten
 *  • `neuer_block` → einen weiteren Block am Tag anlegen
 *  • `bloecke[]`   → mehrere Blöcke des Tages in EINEM atomaren Vorgang (B10a);
 *    überlappende Blöcke werden mit 409 BLOCK_OVERLAP abgelehnt.
 */
export const hrZeitSelbstEintragen = (p: { datum: string; start?: string; ende?: string; pause_min?: string | number; ort?: string; taetigkeit?: string; id?: number; eintrag_id?: string; neuer_block?: boolean; bloecke?: HrZeitBlock[] }) =>
  callBackend<{ person_id: string; datum: string; eintrag_id?: string; eintrag_ids?: string[]; bloecke?: number; ruhezeitVerletzt?: boolean; ruhezeitStd?: string }>("/api/v1/hr/zeit/selbst-eintragen", p);
export const hrZeitComplianceQuittieren = (p: { person_id: string; datum: string; art: string }) =>
  callBackend<{ person_id: string; datum: string; art: string; quittiert: boolean }>("/api/v1/hr/zeit/compliance-quittieren", p);

// ---------- Krankheit ----------
export type HrKrankObj = {
  krankmeldung_id: string; person_id: string; von: string; bis: string; typ: string; grund_freitext: string;
  au_status: "nicht_noetig" | "ausstehend" | "hochgeladen" | "geprueft" | "fehlt" | string;
  au_datei_key: string; folge_von: string; gemeldet_am: string; gesund_gemeldet_am: string;
  status: "laufend" | "beendet" | string; abwesenheit_synced: string;
};

export const hrKrankMelden = (p: { person_id?: string; von: string; bis?: string; grund_freitext?: string; typ?: string }) =>
  callBackend<{ krankmeldung_id: string; au_status: string; au_pflicht_ab_tag: number }>("/api/v1/hr/krank/melden", p);
export const hrKrankAuUpload = (p: { person_id?: string; krankmeldung_id: string; datei_b64: string; content_type: string; dateiname: string }) =>
  callBackend<{ krankmeldung_id: string; au_status: string }>("/api/v1/hr/krank/au-upload", p);
export const hrKrankGesundMelden = (p: { person_id?: string; krankmeldung_id: string; bis?: string }) =>
  callBackend<{ krankmeldung_id: string; status: string; bis: string }>("/api/v1/hr/krank/gesund-melden", p);
export const hrKrankUebersicht = () =>
  callBackend<{ aktuelle: HrKrankObj[]; offene_au: HrKrankObj[]; alle: HrKrankObj[] }>("/api/v1/hr/krank/uebersicht", {});
export const hrKrankMeine = (p: { person_id?: string } = {}) =>
  callBackend<{ person_id: string; meldungen: HrKrankObj[] }>("/api/v1/hr/krank/meine", p);

// ---------- HR-Bereichs-Einstellungen ----------
export type HrSettings = {
  mindestTage: number; zusatzTage: number; vollzeitTage: number;
  prorataAktiv: boolean; prorataRundung: "exakt" | "burlg";
  auPflichtAbTag: number; auAufbewahrungMonate: number;
  uebertragMax: number; uebertragVerfallTag: string;
  tagessatzQuelle: "default" | "real";
  jubilaeumMeilen: number[]; // Dienstjahre, die als Jubiläum gefeiert werden
  vollzeitStunden: number;   // Vollzeit-Wochenstunden = FTE-Referenz (1,0 FTE)
  kostenstellenBereiche: string[]; // konfigurierbare Kostenstellen-Bereiche
};
export const hrSettingsGet = () => callBackend<{ settings: HrSettings; defaults: HrSettings }>("/api/v1/hr/settings/get", {});
export const hrSettingsSet = (p: Partial<HrSettings>) => callBackend<{ settings: HrSettings }>("/api/v1/hr/settings/set", p);

// ---------- FTE je Bereich (Vollzeit-Äquivalente) ----------
export type HrFteEntry = { person_id: string; vollname: string; wochenstunden: number; fte: number; bereich: string; praktikant: boolean };
export type HrFteBereichAgg = { bereich: string; fte: number; count: number };
export type HrFteBereich = { eintraege: HrFteEntry[]; nachBereich: HrFteBereichAgg[]; gesamtFte: number; vollzeitStunden: number; bereiche: string[] };
export const hrFteBereich = () => callBackend<HrFteBereich>("/api/v1/hr/fte-bereich", {});

/** Binärer AU-Abruf (Gesundheitsdatum) — direkter fetch, KEIN Envelope. Liefert Blob. */
export async function hrKrankAuFetch(p: { person_id?: string; krankmeldung_id: string }): Promise<Blob> {
  const token = getToken();
  if (!token) throw new ApiError({ code: "NO_TOKEN", message: "Nicht eingeloggt", statusCode: 401 });
  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/hr/krank/au-fetch`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ payload: p, context: { source: "app_normal" } })
    });
  } catch (err) {
    throw new ApiError({ code: "NETWORK_ERROR", message: (err as Error).message, statusCode: 0 });
  }
  if (!res.ok) {
    let code = "AU_FETCH_FAILED";
    let message = "AU-Abruf fehlgeschlagen";
    try { const j = await res.json() as { error?: { code?: string; message?: string } }; code = j.error?.code ?? code; message = j.error?.message ?? message; } catch { /* kein JSON */ }
    throw new ApiError({ code, message, statusCode: res.status });
  }
  return res.blob();
}
